import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../lib/api";
import { cn } from "../lib/utils";
import { qhIntervalLabel, todayBucharestIso } from "../lib/intervals";
import { useDisplay } from "../stores/display";
import { usePositionFilter } from "../stores/position-filter";
import { parseTsv, volumesToTsv } from "../lib/clipboard-tsv";
import { desktopOnlyRoute } from "../components/ui/mobile-availability-page";

export const Route = createFileRoute("/generator")({
  component: desktopOnlyRoute(GeneratorPage),
});

interface Portfolio {
  id: string;
  brokerCode: string;
  shortName: string | null;
  market: "DA" | "ID";
  ownerEmail: string | null;
  ownerDisplayName: string | null;
}

interface LadderListEntry {
  id: string;
  deliveryDate: string;
  portfolioId: string | null;
  portfolioBrokerCode: string | null;
  name: string;
  tierCount: number;
  updatedAt: string;
  updatedByEmail: string | null;
}

interface LadderRecord {
  id: string;
  deliveryDate: string;
  portfolioId: string | null;
  name: string;
  tiers: number[];
  volumes: number[][];
  createdAt: string;
  updatedAt: string;
}

const DEFAULT_TIERS = [
  -2700, -500.01, -500, -499.99, 3000, 3000.01, 4000, 4000.01, 5000, 5000.01,
  21000,
];

function blankVolumes(tierCount: number): number[][] {
  return Array.from({ length: 96 }, () => new Array(tierCount).fill(0));
}

function GeneratorPage() {
  const queryClient = useQueryClient();
  const zone = useDisplay((s) => s.zone);
  const setZone = useDisplay((s) => s.setZone);
  const portfolioDa = usePositionFilter((s) => s.portfolioDa);
  const setPortfolioDa = usePositionFilter((s) => s.setPortfolioDa);

  const portfoliosQ = useQuery({
    queryKey: ["portfolios"],
    queryFn: () => api<{ portfolios: Portfolio[] }>("/portfolios"),
  });
  const daPortfolios = useMemo(
    () =>
      (portfoliosQ.data?.portfolios ?? []).filter((p) => p.market === "DA"),
    [portfoliosQ.data]
  );

  // Editor state
  const [deliveryDate, setDeliveryDate] = useState(() => todayBucharestIso());
  const [name, setName] = useState("");
  const [tiers, setTiers] = useState<number[]>(DEFAULT_TIERS);
  const [volumes, setVolumes] = useState<number[][]>(() =>
    blankVolumes(DEFAULT_TIERS.length)
  );
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showCopyPanel, setShowCopyPanel] = useState(false);
  const [showTiersPanel, setShowTiersPanel] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // List of saved ladders for the picker (most recent overall + per-day).
  const sameDayQ = useQuery({
    queryKey: ["da-ladders", "exact", deliveryDate, portfolioDa],
    queryFn: () => {
      const params = new URLSearchParams({ deliveryDate });
      if (portfolioDa) params.set("portfolioId", portfolioDa);
      return api<{ ladders: LadderListEntry[] }>(
        `/da-ladders?${params.toString()}`
      );
    },
    enabled: Boolean(deliveryDate),
  });
  const priorQ = useQuery({
    queryKey: ["da-ladders", "prior", deliveryDate, portfolioDa],
    queryFn: () => {
      const params = new URLSearchParams({ priorTo: deliveryDate, limit: "20" });
      if (portfolioDa) params.set("portfolioId", portfolioDa);
      return api<{ ladders: LadderListEntry[] }>(
        `/da-ladders?${params.toString()}`
      );
    },
    enabled: Boolean(deliveryDate) && showCopyPanel,
  });

  // Mutations
  const saveMut = useMutation({
    mutationFn: async (asNew: boolean) => {
      const payload = {
        deliveryDate,
        portfolioId: portfolioDa || null,
        name,
        tiers,
        volumes,
      };
      if (loadedId && !asNew) {
        return api<{ ladder: LadderRecord }>(`/da-ladders/${loadedId}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      }
      return api<{ ladder: LadderRecord }>(`/da-ladders`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },
    onSuccess: (res) => {
      const l = res.ladder;
      setLoadedId(l.id);
      setDirty(false);
      setStatusMsg(`Saved (${new Date().toLocaleTimeString("en-GB")})`);
      setErrorMsg(null);
      void queryClient.invalidateQueries({ queryKey: ["da-ladders"] });
    },
    onError: (err) => {
      setErrorMsg(err instanceof ApiError ? err.message : (err as Error).message);
    },
  });

  const deleteMut = useMutation({
    mutationFn: () =>
      api(`/da-ladders/${loadedId}`, { method: "DELETE" }),
    onSuccess: () => {
      setLoadedId(null);
      setDirty(false);
      setVolumes(blankVolumes(tiers.length));
      setStatusMsg("Deleted");
      void queryClient.invalidateQueries({ queryKey: ["da-ladders"] });
    },
    onError: (err) => {
      setErrorMsg(err instanceof ApiError ? err.message : (err as Error).message);
    },
  });

  // Helpers
  function loadLadder(l: LadderRecord) {
    setLoadedId(l.id);
    setDeliveryDate(l.deliveryDate);
    setName(l.name);
    setTiers(l.tiers);
    setVolumes(l.volumes);
    setDirty(false);
    setStatusMsg(`Loaded "${l.name || "(unnamed)"}" — ${l.deliveryDate}`);
    setErrorMsg(null);
  }

  async function handleLoadById(id: string) {
    try {
      const res = await api<{ ladder: LadderRecord }>(`/da-ladders/${id}`);
      loadLadder(res.ladder);
    } catch (e) {
      setErrorMsg(e instanceof ApiError ? e.message : (e as Error).message);
    }
  }

  function handleCopyFrom(entry: LadderListEntry) {
    void (async () => {
      try {
        const res = await api<{ ladder: LadderRecord }>(`/da-ladders/${entry.id}`);
        // Keep current delivery date and name; copy tiers + volumes.
        setTiers(res.ladder.tiers);
        setVolumes(res.ladder.volumes);
        setLoadedId(null); // ensures next save creates a new row
        setDirty(true);
        setStatusMsg(
          `Copied tiers + volumes from ${entry.deliveryDate}${entry.name ? ` "${entry.name}"` : ""}`
        );
        setErrorMsg(null);
        setShowCopyPanel(false);
      } catch (e) {
        setErrorMsg(e instanceof ApiError ? e.message : (e as Error).message);
      }
    })();
  }

  function setVolumeCell(qh: number, k: number, value: number) {
    const v = Number.isFinite(value) ? Math.round(value * 10) / 10 : 0;
    setVolumes((prev) => {
      const next = prev.slice();
      next[qh] = propagateRow(next[qh]!, k, v);
      return next;
    });
    setDirty(true);
  }

  function fillColumn(k: number, value: number) {
    const v = Math.round(value * 10) / 10;
    setVolumes((prev) => prev.map((row) => propagateRow(row, k, v)));
    setDirty(true);
  }

  function fillCells(cells: { qh: number; k: number }[], value: number) {
    if (cells.length === 0) return;
    const v = Math.round(value * 10) / 10;
    setVolumes((prev) => {
      const next = prev.slice();
      for (const c of cells) {
        next[c.qh] = propagateRow(next[c.qh]!, c.k, v);
      }
      return next;
    });
    setDirty(true);
  }

  function clearAll() {
    setVolumes(blankVolumes(tiers.length));
    setDirty(true);
  }

  function applyTierChange(newTiers: number[]) {
    if (newTiers.length === tiers.length) {
      setTiers(newTiers);
      setDirty(true);
      return;
    }
    // Reshape volumes to new tier count: keep overlapping columns, pad with zeros.
    setVolumes((prev) =>
      prev.map((row) => {
        const next = new Array(newTiers.length).fill(0);
        for (let k = 0; k < Math.min(row.length, newTiers.length); k++) {
          next[k] = row[k]!;
        }
        return next;
      })
    );
    setTiers(newTiers);
    setDirty(true);
  }

  // xlsx import
  async function handleImportXlsx(file: File) {
    setStatusMsg("Importing…");
    setErrorMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/da-ladders/parse-xlsx", {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? res.statusText);
      const data = body as {
        tiers: number[];
        volumes: number[][];
        deliveryDate: string | null;
        sheetName: string;
        warnings: string[];
      };
      setTiers(data.tiers);
      setVolumes(data.volumes);
      if (data.deliveryDate) setDeliveryDate(data.deliveryDate);
      setLoadedId(null);
      setDirty(true);
      setStatusMsg(
        `Imported ${data.sheetName}${data.warnings.length ? ` (${data.warnings.length} warnings)` : ""}`
      );
    } catch (e) {
      setErrorMsg((e as Error).message);
      setStatusMsg(null);
    }
  }

  // xlsx export
  function handleExportXlsx() {
    if (!loadedId || dirty) {
      saveMut.mutate(false, {
        onSuccess: (res) => {
          window.open(`/api/da-ladders/${res.ladder.id}/export.xlsx`, "_blank");
        },
      });
      return;
    }
    window.open(`/api/da-ladders/${loadedId}/export.xlsx`, "_blank");
  }

  // Clipboard
  async function handleCopyToClipboard() {
    try {
      await navigator.clipboard.writeText(volumesToTsv(volumes));
      setStatusMsg("Copied 96×N volume matrix to clipboard (TSV)");
    } catch (e) {
      setErrorMsg((e as Error).message);
    }
  }

  async function handlePasteFromClipboard() {
    try {
      const text = await navigator.clipboard.readText();
      const { volumes: v, warnings } = parseTsv(text, tiers.length);
      setVolumes(v);
      setDirty(true);
      setStatusMsg(
        `Pasted from clipboard${warnings.length ? ` (${warnings.length} warnings)` : ""}`
      );
      setErrorMsg(null);
    } catch (e) {
      setErrorMsg((e as Error).message);
    }
  }

  // Auto-load if there's a single existing ladder for (deliveryDate, portfolio)
  useEffect(() => {
    if (loadedId) return;
    const list = sameDayQ.data?.ladders;
    if (!list || list.length === 0) return;
    if (list.length === 1) {
      void handleLoadById(list[0]!.id);
    }
    // > 1: leave it to the user to pick via copy panel or list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameDayQ.data, deliveryDate, portfolioDa]);

  const sameDayList = sameDayQ.data?.ladders ?? [];

  return (
    <div className="p-4 space-y-4">
      {/* Filter / actions bar */}
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Delivery date">
          <input
            type="date"
            value={deliveryDate}
            onChange={(e) => {
              setDeliveryDate(e.target.value);
              setLoadedId(null);
              setDirty(false);
            }}
            className="bg-transparent border border-border rounded px-2 py-1 text-sm"
          />
        </Field>
        <Field label="DA portfolio">
          <select
            value={portfolioDa}
            onChange={(e) => {
              setPortfolioDa(e.target.value);
              setLoadedId(null);
            }}
            className="bg-transparent border border-border rounded px-2 py-1 text-sm min-w-[18rem]"
          >
            <option value="">(none)</option>
            {daPortfolios.map((p) => (
              <option key={p.id} value={p.id}>
                {`${p.ownerDisplayName ?? p.ownerEmail ?? "?"} · ${p.brokerCode}`}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Name (scenario)">
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setDirty(true);
            }}
            placeholder="(blank = canonical)"
            className="bg-transparent border border-border rounded px-2 py-1 text-sm w-56"
          />
        </Field>
        <Field label="Zone">
          <select
            value={zone}
            onChange={(e) => setZone(e.target.value as "CET" | "EET")}
            className="bg-transparent border border-border rounded px-2 py-1 text-sm"
          >
            <option value="CET">CET</option>
            <option value="EET">EET</option>
          </select>
        </Field>
        <div className="flex gap-2 ml-auto items-center text-xs">
          <button
            type="button"
            onClick={() => saveMut.mutate(false)}
            disabled={saveMut.isPending}
            className={cn(
              "border rounded px-3 py-1.5",
              dirty
                ? "border-accent text-accent hover:bg-accent/10"
                : "border-border text-muted"
            )}
          >
            {loadedId ? "Save" : "Create"}
          </button>
          {loadedId && (
            <>
              <button
                type="button"
                onClick={() => saveMut.mutate(true)}
                disabled={saveMut.isPending}
                className="border border-border rounded px-2 py-1.5 hover:border-accent hover:text-fg"
              >
                Save as new
              </button>
              <button
                type="button"
                onClick={() => {
                  if (confirm("Delete this ladder?")) deleteMut.mutate();
                }}
                disabled={deleteMut.isPending}
                className="border border-border rounded px-2 py-1.5 hover:border-negRed hover:text-negRed"
              >
                Delete
              </button>
            </>
          )}
          <span className="border-l border-border/40 h-6 mx-1" />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="border border-border rounded px-2 py-1.5 hover:border-accent hover:text-fg"
          >
            Import xlsx
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleImportXlsx(f);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            onClick={handlePasteFromClipboard}
            className="border border-border rounded px-2 py-1.5 hover:border-accent hover:text-fg"
          >
            Paste TSV
          </button>
          <button
            type="button"
            onClick={handleExportXlsx}
            className="border border-border rounded px-2 py-1.5 hover:border-accent hover:text-fg"
          >
            Export xlsx
          </button>
          <button
            type="button"
            onClick={handleCopyToClipboard}
            className="border border-border rounded px-2 py-1.5 hover:border-accent hover:text-fg"
          >
            Copy TSV
          </button>
        </div>
      </div>

      {/* Status / errors */}
      <div className="flex items-center justify-between text-xs min-h-[1.25rem]">
        <span className="text-muted">
          {loadedId ? (
            <>
              Editing ladder <span className="font-mono">{loadedId.slice(0, 8)}…</span>
              {dirty && <span className="ml-2 text-warnOrange">• unsaved changes</span>}
            </>
          ) : (
            <>New ladder (not saved)</>
          )}
        </span>
        <span className="flex items-center gap-3">
          {statusMsg && <span className="text-posGreen">{statusMsg}</span>}
          {errorMsg && <span className="text-negRed">{errorMsg}</span>}
        </span>
      </div>

      {/* Same-day list — quick switcher when multiple scenarios exist */}
      {sameDayList.length > 0 && (
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span className="text-muted">Saved for {deliveryDate}:</span>
          {sameDayList.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => handleLoadById(l.id)}
              className={cn(
                "border rounded px-2 py-1 hover:border-accent",
                loadedId === l.id ? "border-accent text-accent" : "border-border"
              )}
            >
              {l.name || "(canonical)"}
            </button>
          ))}
        </div>
      )}

      {/* Copy from prior day */}
      <details
        className="border border-border/60 rounded"
        open={showCopyPanel}
        onToggle={(e) => setShowCopyPanel((e.target as HTMLDetailsElement).open)}
      >
        <summary className="px-3 py-2 cursor-pointer text-xs">
          Copy from a previous day
        </summary>
        <div className="px-3 py-2 space-y-1 text-xs">
          {priorQ.isLoading && <div className="text-muted">Loading…</div>}
          {priorQ.data && priorQ.data.ladders.length === 0 && (
            <div className="text-muted">No ladders saved for prior dates.</div>
          )}
          {priorQ.data?.ladders.map((l) => (
            <div
              key={l.id}
              className="flex items-center justify-between border border-border/40 rounded px-2 py-1 hover:border-accent/60"
            >
              <span className="font-mono">
                {l.deliveryDate}
                {l.name ? ` · ${l.name}` : ""}
                {l.portfolioBrokerCode ? ` · ${l.portfolioBrokerCode}` : ""}
              </span>
              <button
                type="button"
                onClick={() => handleCopyFrom(l)}
                className="text-accent hover:underline"
              >
                Copy here
              </button>
            </div>
          ))}
        </div>
      </details>

      {/* Tier editor */}
      <details
        className="border border-border/60 rounded"
        open={showTiersPanel}
        onToggle={(e) =>
          setShowTiersPanel((e.target as HTMLDetailsElement).open)
        }
      >
        <summary className="px-3 py-2 cursor-pointer text-xs">
          Tiers ({tiers.length} columns)
        </summary>
        <div className="px-3 py-2 space-y-2 text-xs">
          <TierEditor tiers={tiers} onChange={applyTierChange} />
        </div>
      </details>

      <div className="flex items-center gap-2 text-xs">
        <button
          type="button"
          onClick={clearAll}
          className="border border-border rounded px-2 py-1 hover:border-negRed hover:text-negRed"
        >
          Clear all volumes
        </button>
      </div>

      {/* The big grid */}
      <LadderGrid
        tiers={tiers}
        volumes={volumes}
        zone={zone}
        onCellChange={setVolumeCell}
        onFillColumn={fillColumn}
        onFillCells={fillCells}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="text-xs text-muted flex flex-col gap-1">
      {label}
      {children}
    </label>
  );
}

// BRM DA market accepts step-function bid curves bounded by these outer
// limits. Every tier list MUST start at -2700 and end at 21000.
const TIER_OUTER_MIN = -2700;
const TIER_OUTER_MAX = 21000;

function normaliseTiers(values: number[]): number[] {
  // Drop anything outside the outer band; sort; dedupe; pin -2700 / 21000.
  const inner = values
    .filter((n) => Number.isFinite(n) && n > TIER_OUTER_MIN && n < TIER_OUTER_MAX)
    .map((n) => Math.round(n * 100) / 100); // tiers come in 0.01-resolution
  inner.sort((a, b) => a - b);
  const deduped: number[] = [];
  for (const n of inner) {
    if (deduped.length === 0 || Math.abs(deduped[deduped.length - 1]! - n) > 1e-9) {
      deduped.push(n);
    }
  }
  return [TIER_OUTER_MIN, ...deduped, TIER_OUTER_MAX];
}

function buildBracketedTiers(pivots: number[]): number[] {
  const inner: number[] = [];
  for (const p of pivots) {
    if (!Number.isFinite(p)) continue;
    inner.push(p - 0.01, p, p + 0.01);
  }
  return normaliseTiers(inner);
}

function TierEditor({
  tiers,
  onChange,
}: {
  tiers: number[];
  onChange: (next: number[]) => void;
}) {
  const [draft, setDraft] = useState(() => tiers.map((t) => String(t)).join(", "));
  useEffect(() => {
    setDraft(tiers.map((t) => String(t)).join(", "));
  }, [tiers]);
  return (
    <div className="space-y-2">
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="w-full bg-transparent border border-border rounded px-2 py-1 font-mono text-xs"
        rows={3}
      />
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => {
            const parts = draft.split(/[,\s]+/).filter((s) => s.length > 0);
            const nums = parts.map((s) => Number(s.replace(",", ".")));
            if (nums.some((n) => !Number.isFinite(n))) {
              alert("Some tier values are not numeric");
              return;
            }
            onChange(normaliseTiers(nums));
          }}
          className="border border-accent text-accent rounded px-2 py-1 hover:bg-accent/10"
        >
          Apply
        </button>
        <button
          type="button"
          onClick={() => {
            // Pivot helper: prompt for pivot prices and bracket each with ±0.01.
            // Outer limits are always -2700 / +21000 (hard exchange limits).
            const raw = prompt(
              "Enter pivot prices separated by commas (e.g. -500, 3000, 4000, 5000). Outer limits stay -2700 / +21000."
            );
            if (!raw) return;
            const pivots = raw
              .split(/[,\s]+/)
              .filter((s) => s.length > 0)
              .map((s) => Number(s.replace(",", ".")))
              .filter((n) => Number.isFinite(n));
            if (pivots.length === 0) {
              alert("No valid pivot prices were given");
              return;
            }
            onChange(buildBracketedTiers(pivots));
          }}
          className="border border-border rounded px-2 py-1 hover:border-accent hover:text-fg"
        >
          ±0.01 bracket helper
        </button>
        <span className="text-muted">
          Outer limits are pinned to {TIER_OUTER_MIN} and +{TIER_OUTER_MAX}; tiers
          outside that range are dropped on Apply.
        </span>
      </div>
    </div>
  );
}

interface Selection {
  qh1: number;
  k1: number;
  qh2: number;
  k2: number;
}

function selectionBounds(s: Selection) {
  return {
    qLo: Math.min(s.qh1, s.qh2),
    qHi: Math.max(s.qh1, s.qh2),
    kLo: Math.min(s.k1, s.k2),
    kHi: Math.max(s.k1, s.k2),
  };
}

function selectionSize(s: Selection): number {
  const b = selectionBounds(s);
  return (b.qHi - b.qLo + 1) * (b.kHi - b.kLo + 1);
}

// Round to 1 decimal place. The DA exchange accepts up to 0.1 MW resolution.
function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

// Set row[k] = value, then auto-extend the row to maintain a valid
// step-function bid curve: sells (value < 0) extend to higher-price tiers
// (k+1..end); buys (value > 0) extend to lower-price tiers (0..k-1). Cells
// that already hold a deeper offer than `value` are left alone, so the rule
// is idempotent and order-independent.
function propagateRow(row: number[], k: number, value: number): number[] {
  const next = row.slice();
  next[k] = value;
  if (value < 0) {
    for (let j = k + 1; j < next.length; j++) {
      if (next[j]! > value) next[j] = value;
    }
  } else if (value > 0) {
    for (let j = k - 1; j >= 0; j--) {
      if (next[j]! < value) next[j] = value;
    }
  }
  return next;
}

function LadderGrid({
  tiers,
  volumes,
  zone,
  onCellChange,
  onFillColumn,
  onFillCells,
}: {
  tiers: number[];
  volumes: number[][];
  zone: "CET" | "EET";
  onCellChange: (qh: number, k: number, v: number) => void;
  onFillColumn: (k: number, v: number) => void;
  onFillCells: (cells: { qh: number; k: number }[], v: number) => void;
}) {
  const [sel, setSel] = useState<Selection | null>(null);
  const [dragging, setDragging] = useState(false);
  const [fillValue, setFillValue] = useState("");
  const fillInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onUp = () => setDragging(false);
    document.addEventListener("mouseup", onUp);
    return () => document.removeEventListener("mouseup", onUp);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSel(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const inSel = (qh: number, k: number): boolean => {
    if (!sel) return false;
    const b = selectionBounds(sel);
    return qh >= b.qLo && qh <= b.qHi && k >= b.kLo && k <= b.kHi;
  };

  function applyFill() {
    if (!sel) return;
    const n = Number(fillValue.replace(",", ".").trim());
    if (!Number.isFinite(n)) return;
    const rounded = round1(n);
    const b = selectionBounds(sel);
    const cells: { qh: number; k: number }[] = [];
    for (let qh = b.qLo; qh <= b.qHi; qh++) {
      for (let k = b.kLo; k <= b.kHi; k++) {
        cells.push({ qh, k });
      }
    }
    onFillCells(cells, rounded);
    setFillValue(String(rounded));
  }

  const showBar = sel && selectionSize(sel) > 1;

  return (
    <div className="space-y-2">
      <div
        className={cn(
          "flex items-center gap-2 text-xs border rounded p-2",
          showBar
            ? "border-accent/60 bg-accent/5"
            : "border-border/40 text-muted"
        )}
      >
        {sel ? (
          <>
            <span>
              Selection: {selectionSize(sel)} cells (
              QH-{selectionBounds(sel).qLo + 1}..QH-{selectionBounds(sel).qHi + 1}
              {", "}
              tier {selectionBounds(sel).kLo + 1}..{selectionBounds(sel).kHi + 1})
            </span>
            <span className="ml-auto flex items-center gap-1">
              <input
                ref={fillInputRef}
                type="text"
                inputMode="decimal"
                value={fillValue}
                placeholder="value"
                onChange={(e) => setFillValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    applyFill();
                    e.preventDefault();
                  }
                }}
                className="bg-transparent border border-border rounded px-2 py-1 w-24 text-right"
              />
              <button
                type="button"
                onClick={applyFill}
                className="border border-accent text-accent rounded px-2 py-1 hover:bg-accent/10"
              >
                Fill
              </button>
              <button
                type="button"
                onClick={() => {
                  setFillValue("0");
                  setTimeout(() => fillInputRef.current?.focus(), 0);
                }}
                className="border border-border rounded px-2 py-1 hover:border-accent hover:text-fg"
              >
                Zero
              </button>
              <button
                type="button"
                onClick={() => setSel(null)}
                className="border border-border rounded px-2 py-1 hover:border-accent hover:text-fg"
              >
                Clear
              </button>
            </span>
          </>
        ) : (
          <>
            <span>
              Drag to select a range, then enter a value to fill. Sells
              (negative) auto-extend to higher-price tiers; buys (positive) to
              lower. Existing deeper offers are preserved. Esc clears.
            </span>
            <span className="ml-auto">
              Tip: click a column header to fill all 96 rows.
            </span>
          </>
        )}
      </div>

      <div className="overflow-auto border border-border/60 rounded-md max-h-[calc(100vh-26rem)]">
        <table className="min-w-full font-mono text-[11px] border-separate border-spacing-0 select-none">
          <thead>
            <tr className="text-muted">
              <th className="px-2 py-1.5 sticky top-0 z-10 bg-bg text-left whitespace-nowrap border-b border-border/60">
                QH
              </th>
              <th className="px-2 py-1.5 sticky top-0 z-10 bg-bg text-left whitespace-nowrap border-b border-border/60">
                Interval ({zone})
              </th>
              {tiers.map((t, k) => (
                <th
                  key={k}
                  className="px-2 py-1.5 sticky top-0 z-10 bg-bg text-right whitespace-nowrap border-b border-border/60"
                  title={`Click to fill all 96 rows with one value`}
                >
                  <button
                    type="button"
                    className="text-fg hover:text-accent"
                    onClick={() => {
                      const raw = prompt(
                        `Fill column for tier ${t} with which value?`,
                        "0"
                      );
                      if (raw === null) return;
                      const n = Number(raw.replace(",", "."));
                      if (!Number.isFinite(n)) return;
                      onFillColumn(k, round1(n));
                    }}
                  >
                    {t}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 96 }, (_, qh) => (
              <tr
                key={qh}
                className={cn(
                  "hover:bg-border/10",
                  qh % 2 === 1 && "bg-border/[0.03]"
                )}
              >
                <td className="px-2 py-1 whitespace-nowrap border-t border-border/30 text-muted">
                  QH-{qh + 1}
                </td>
                <td className="px-2 py-1 whitespace-nowrap border-t border-border/30 text-muted">
                  {qhIntervalLabel(qh + 1, zone)}
                </td>
                {tiers.map((_t, k) => {
                  const v = volumes[qh]?.[k] ?? 0;
                  const selected = inSel(qh, k);
                  return (
                    <td
                      key={k}
                      onMouseDown={() => {
                        setSel({ qh1: qh, k1: k, qh2: qh, k2: k });
                        setDragging(true);
                      }}
                      onMouseEnter={() => {
                        if (!dragging) return;
                        setSel((s) =>
                          s ? { ...s, qh2: qh, k2: k } : { qh1: qh, k1: k, qh2: qh, k2: k }
                        );
                      }}
                      className={cn(
                        "px-1 py-0.5 border-t border-border/30 text-right",
                        selected && "bg-accent/20 outline outline-1 outline-accent/40"
                      )}
                    >
                      <CellInput
                        value={v}
                        onCommit={(n) => onCellChange(qh, k, n)}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CellInput({
  value,
  onCommit,
}: {
  value: number;
  onCommit: (n: number) => void;
}) {
  const [draft, setDraft] = useState(() => formatVal(value));
  const lastValueRef = useRef(value);
  useEffect(() => {
    if (value !== lastValueRef.current) {
      setDraft(formatVal(value));
      lastValueRef.current = value;
    }
  }, [value]);
  return (
    <input
      type="text"
      inputMode="decimal"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => commit()}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          commit();
          e.preventDefault();
        } else if (e.key === "Escape") {
          setDraft(formatVal(value));
          (e.target as HTMLInputElement).blur();
        }
      }}
      className={cn(
        "w-16 bg-transparent text-right outline-none border border-transparent",
        "hover:border-border/50 focus:border-accent rounded px-1",
        value > 0 && "text-posGreen font-semibold",
        value < 0 && "text-negRed font-semibold",
        value === 0 && "text-fg/40"
      )}
    />
  );
  function commit() {
    const raw = Number(draft.replace(",", ".").trim());
    if (!Number.isFinite(raw)) {
      setDraft(formatVal(value));
      return;
    }
    // Restrict to 1 decimal place — the DA exchange accepts 0.1 MW resolution.
    const n = Math.round(raw * 10) / 10;
    if (n !== value) onCommit(n);
    setDraft(formatVal(n));
    lastValueRef.current = n;
  }
}

function formatVal(n: number): string {
  if (n === 0) return "0";
  // Always display with at most 1 decimal (matches the input restriction).
  const fixed = (Math.round(n * 10) / 10).toFixed(1);
  return fixed.replace(/\.0$/, "");
}
